/**
 * Hook for managing onboarding token validation and countdown timer.
 *
 * This hook handles:
 * - Extracting token from URL and clearing it for security
 * - Server-side token validation
 * - Countdown timer with warning state
 * - Session state management
 *
 * Uses useReducer for cleaner state management with explicit state transitions.
 */

import * as React from "react";
import { useSearchParams } from "react-router-dom";
import { TOKEN_CONFIG } from "~/server/constants";

// =============================================================================
// Types
// =============================================================================

/**
 * Client-side token data received from server validation.
 */
export interface ClientTokenData {
  sessionId: string;        // Raw token (stored locally)
  onboardingId: string;     // Onboarding ID from george-api
  enterpriseId: string;     // Company ID
  partialRfc: string;
  partialRazonSocial: string;
  rfc: string;              // Full RFC for form pre-fill (readonly field)
  razonSocial: string;      // Full razonSocial for form pre-fill (readonly field)
  regimenFiscal?: string;
  expiresAt: number;
  remainingSeconds: number;
}

/**
 * Token error types.
 */
export type TokenError = "expired" | "invalid" | "used" | "missing";

/**
 * Token validation state.
 */
type TokenState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "valid"; payload: ClientTokenData; remainingTime: number; isPaused: boolean }
  | { status: "invalid"; error: TokenError }
  | { status: "expired" };

/**
 * Actions for token state transitions.
 */
type TokenAction =
  | { type: "START_VALIDATION" }
  | { type: "VALIDATION_SUCCESS"; payload: ClientTokenData; remainingTime: number }
  | { type: "VALIDATION_FAILED"; error: TokenError }
  | { type: "TOKEN_EXPIRED" }
  | { type: "TICK"; remainingTime: number }
  | { type: "MISSING_TOKEN" }
  | { type: "TOGGLE_PAUSE" };

/**
 * Hook return type.
 */
export interface UseOnboardingTokenResult {
  isLoading: boolean;
  isValid: boolean;
  isExpired: boolean;
  error: TokenError | null;
  payload: ClientTokenData | null;
  remainingTime: number;
  formattedTime: string;
  isWarning: boolean;
  tokenId: string | null;
  onboardingId: string | null;
  isPaused: boolean;
  togglePause: () => void;
}

// =============================================================================
// Reducer
// =============================================================================

function tokenReducer(state: TokenState, action: TokenAction): TokenState {
  switch (action.type) {
    case "START_VALIDATION":
      return { status: "loading" };

    case "VALIDATION_SUCCESS":
      return {
        status: "valid",
        payload: action.payload,
        remainingTime: action.remainingTime,
        isPaused: false,
      };

    case "VALIDATION_FAILED":
      if (action.error === "expired") {
        return { status: "expired" };
      }
      return { status: "invalid", error: action.error };

    case "TOKEN_EXPIRED":
      return { status: "expired" };

    case "TICK":
      if (state.status !== "valid") return state;
      if (state.isPaused) return state; // Don't tick when paused
      if (action.remainingTime <= 0) {
        return { status: "expired" };
      }
      return { ...state, remainingTime: action.remainingTime };

    case "TOGGLE_PAUSE":
      if (state.status !== "valid") return state;
      return { ...state, isPaused: !state.isPaused };

    case "MISSING_TOKEN":
      return { status: "invalid", error: "missing" };

    default:
      return state;
  }
}

// =============================================================================
// Helpers
// =============================================================================

/**
 * Get Onboarding API URL from environment (Lambda SST).
 */
function getOnboardingApiUrl(): string {
  if (typeof import.meta !== "undefined" && import.meta.env?.VITE_ONBOARDING_API_URL) {
    return import.meta.env.VITE_ONBOARDING_API_URL;
  }
  // Fallback to local development
  return "http://localhost:3001";
}

/**
 * Format remaining time as MM:SS.
 */
function formatTime(remainingTimeMs: number): string {
  const totalSeconds = Math.max(0, Math.floor(remainingTimeMs / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
}

// =============================================================================
// Hook
// =============================================================================

export function useOnboardingToken(): UseOnboardingTokenResult {
  const [searchParams, setSearchParams] = useSearchParams();
  const [storedToken, setStoredToken] = React.useState<string | null>(null);
  const [state, dispatch] = React.useReducer(tokenReducer, { status: "idle" });

  // SECURITY: Extract token from URL and immediately clear it
  // This prevents token from appearing in browser history, referrer headers, and analytics
  React.useEffect(() => {
    const urlToken = searchParams.get("token");
    if (urlToken) {
      setStoredToken(urlToken);
      searchParams.delete("token");
      setSearchParams(searchParams, { replace: true });
    } else if (!storedToken && state.status === "idle") {
      dispatch({ type: "MISSING_TOKEN" });
    }
  }, [searchParams, setSearchParams, storedToken, state.status]);

  // Validate token with Lambda SST
  React.useEffect(() => {
    if (!storedToken) return;

    const validateWithServer = async () => {
      dispatch({ type: "START_VALIDATION" });

      try {
        const apiUrl = getOnboardingApiUrl();
        // Lambda SST endpoint for token validation
        // This also atomically transitions session to IN_PROGRESS
        const response = await fetch(`${apiUrl}/api/onboarding/validate-token`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ token: storedToken }),
        });

        const result = await response.json();

        if (response.ok && result.valid) {
          // Lambda SST returns: valid, status, data: { sessionId, enterpriseId, partialRfc, ... }
          const { data } = result;

          const tokenData: ClientTokenData = {
            sessionId: data.sessionId,
            onboardingId: data.sessionId, // Use sessionId as onboardingId for Lambda SST
            enterpriseId: data.enterpriseId,
            partialRfc: data.partialRfc,
            partialRazonSocial: data.partialRazonSocial,
            rfc: data.rfc,  // Full RFC for form pre-fill (readonly)
            razonSocial: data.razonSocial,  // Full razonSocial for form pre-fill (readonly)
            regimenFiscal: data.regimenFiscal,
            expiresAt: data.expiresAt,
            remainingSeconds: data.remainingSeconds,
          };

          dispatch({
            type: "VALIDATION_SUCCESS",
            payload: tokenData,
            remainingTime: data.remainingSeconds * 1000,
          });
        } else {
          // Map Lambda SST error status to TokenError
          let error: TokenError;
          if (result.status === "expired") {
            error = "expired";
          } else if (result.status === "completed") {
            error = "used";
          } else {
            error = "invalid";
          }
          dispatch({ type: "VALIDATION_FAILED", error });
        }
      } catch (err) {
        if (import.meta.env.DEV) {
          console.error("[useOnboardingToken] Validation failed:", err);
        }
        dispatch({ type: "VALIDATION_FAILED", error: "invalid" });
      }
    };

    validateWithServer();
  }, [storedToken]);

  // Countdown timer
  const timerRemainingTime = state.status === "valid" ? state.remainingTime : 0;
  const timerIsPaused = state.status === "valid" ? state.isPaused : false;

  React.useEffect(() => {
    if (state.status !== "valid" || timerRemainingTime <= 0 || timerIsPaused) return;

    const interval = setInterval(() => {
      dispatch({
        type: "TICK",
        remainingTime: timerRemainingTime - 1000,
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [state.status, timerRemainingTime, timerIsPaused]);

  // Toggle pause callback
  const togglePause = React.useCallback(() => {
    dispatch({ type: "TOGGLE_PAUSE" });
  }, []);

  // Compute derived values
  const isLoading = state.status === "loading" || state.status === "idle";
  const isValid = state.status === "valid";
  const isExpired = state.status === "expired";
  const remainingTime = state.status === "valid" ? state.remainingTime : 0;
  const isPaused = state.status === "valid" ? state.isPaused : false;
  const payload = state.status === "valid" ? state.payload : null;
  const error: TokenError | null =
    state.status === "invalid"
      ? state.error
      : state.status === "expired"
        ? "expired"
        : null;

  return {
    isLoading,
    isValid,
    isExpired,
    error,
    payload,
    remainingTime,
    formattedTime: formatTime(remainingTime),
    isWarning: remainingTime > 0 && remainingTime <= TOKEN_CONFIG.WARNING_THRESHOLD_MS,
    tokenId: payload?.sessionId || null,
    onboardingId: payload?.onboardingId || null,
    isPaused,
    togglePause,
  };
}
